<template>
  <div>
    <popup-add-product
      v-if="stockCount"
      ref="popupAddProduct"
      @productSelected="productSelected"
      :existingProducts="stockCount.stockCountLines"
      :assignedStockProducts="assignedStockProducts"
    />

    <RightSidebar :isActive.sync="isActive" size="large">
      <template #header>
        <img width="40" :src="OrderIcon" class="mr-4" />
        <div>
          <div class="flex items-center">
            <div><h4>Stock count</h4></div>
            <vs-chip
              v-if="stockCount"
              :color="stateMapping[stockCount.status].color"
              class="ml-2"
            >
              {{ stateMapping[stockCount.status].label }}
            </vs-chip>
          </div>
          <small v-if="stockCount">{{ stockCount.id }}</small>
        </div>
      </template>

      <template #content>
        <Loading v-if="isLoading" />
        <div v-else>
          <section class="px-6 pt-6 grid grid-cols-4 gap-x-8">
            <SidebarItem label="Deadline">
              <DateInput
                v-model="stockCount.deadlineDate"
                placeholder="Deadline (optional)"
                class="w-full"
                :disabled="!isEditable"
              />
            </SidebarItem>
            <SidebarItem label="Storage">
              <vs-select
                v-model="stockCount.storageId"
                autocomplete
                class="w-full"
                placeholder="Select a storage"
                :disabled="!isEditable"
              >
                <vs-select-item
                  :key="item.storageId.id"
                  :value="item.storageId.id"
                  :text="item.name"
                  v-for="item in venue.storages"
                />
              </vs-select>
            </SidebarItem>

            <SidebarItem label="Count type">
              {{
                stockCount.isCreatedManually
                  ? "Forced count"
                  : "Automatic count"
              }}
            </SidebarItem>

            <vs-divider class="col-span-full" />

            <SidebarItem label="Created">
              <div class="flex flex-col">
                <div class="flex">
                  <feather-icon
                    class="mr-1"
                    icon="CalendarIcon"
                    svgClasses="text-grey w-5 h-5"
                  />
                  {{ stockCount.createdAction.timestamp | formatDate }}
                </div>
                <div class="text-grey ml-6">
                  By {{ stockCount.createdAction.userDisplayName }}
                </div>
              </div>
            </SidebarItem>
          </section>

          <h3 class="ml-4 mt-4 mb-2">Stock products to count</h3>

          <div class="mx-6 data-list-list-view table-primary-bg compact-table">
            <vs-table :data="stockCount.stockCountLines">
              <!--     <template slot="header">
                  <h3 class="ml-6 mb-1">Products</h3>
                </template> -->
              <template slot="thead">
                <vs-th>Product</vs-th>
                <vs-th>Counted amount</vs-th>
                <vs-th>Actual amount</vs-th>
                <vs-th>Deviation</vs-th>
                <vs-th v-if="isEditable"></vs-th>
              </template>

              <template slot-scope="{ data }">
                <vs-tr
                  :data="tr"
                  :key="tr.id"
                  v-for="tr in data"
                  :state="tr.orderCases == 0 ? 'danger' : ''"
                >
                  <vs-td>
                    <div
                      class="product-name font-medium truncate flex flex-row items-center"
                    >
                      <div class="flex flex-col">
                        <div
                          class="whitespace-nowrap overflow-hidden truncate"
                          style="width: 206px"
                        >
                          {{ tr.stockProduct.name }}
                        </div>
                        <div class="flex items-center">
                          <div class="text-sm text-grey">
                            {{ tr.stockProduct.number }} •
                            {{ tr.stockProduct.productGroupId }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </vs-td>

                  <vs-td>
                    <div v-if="tr.hasBeenCounted">
                      {{ tr.amount | formatDecimal(2) }}
                      {{ tr.stockProduct.productUnit }}
                    </div>
                    <div v-else class="text-grey">
                      <i>Not counted</i>
                    </div>
                  </vs-td>

                  <vs-td>
                    <div v-if="tr.hasBeenCounted">
                      {{ tr.actualStockCount | formatDecimal(2) }}
                      {{ tr.stockProduct.productUnit }}
                    </div>
                  </vs-td>
                  <vs-td>
                    <div v-if="tr.hasBeenCounted">
                      {{ tr.deviation | formatDecimal(2) }}
                      {{ tr.stockProduct.productUnit }}
                    </div>
                  </vs-td>

                  <vs-td v-if="isEditable">
                    <feather-icon
                      @click="removeProduct(tr.stockProductId)"
                      icon="TrashIcon"
                      class="cursor-pointer mr-2"
                      svgClasses="h-5 w-5 stroke-2"
                    />
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>
            <!-- <p v-for="id in stocktake.stockProductIds" :key="id">{{ id }}</p> -->
            <div class="flex justify-end mt-2">
              <vs-button
                v-if="isEditable"
                class="ml-2 py-2 px-3"
                @click="$refs.popupAddProduct.show()"
                type="border"
              >
                <feather-icon icon="PlusIcon" svgClasses="w-4 h-4 pt-1" />
                <span class="ml-1">Add stock product</span>
              </vs-button>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <vs-button
          class="mr-4"
          @click="save"
          :disabled="!isFormValid"
          v-if="isEditable"
        >
          Save
        </vs-button>

        <vs-button
          v-if="isEditable"
          type="border"
          class="ml-auto"
          @click="activateStockCount"
          >Activate stock count</vs-button
        >
        <!-- <vs-button class="mr-4" @click="onClickPreview"> Preview </vs-button> -->
        <!-- <vs-button color="danger" v-if="isDeleteable" @click="confirmDelete">
          Delete
        </vs-button> -->
      </template>
    </RightSidebar>
  </div>
</template>

<script>
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import OrderIcon from "@/assets/images/order-icon.png";
import { mapGetters } from "vuex";
import PopupAddProduct from "./PopupAddAssignedStockProduct.vue";
import RightSidebar from "@/components/Custom/RightSidebar.vue";
import NumberInput from "@/components/Custom/NumberInput.vue";
import DateInput from "@/components/Custom/DateInput.vue";
import SidebarItem from "@/components/Custom/SidebarItem.vue";

export default {
  components: {
    VuePerfectScrollbar,
    PopupAddProduct,
    RightSidebar,
    NumberInput,
    DateInput,
    SidebarItem,
  },
  data() {
    return {
      OrderIcon,

      isActive: false,
      isLoading: true,

      stockCount: null,

      venue: null,
    };
  },
  computed: {
    ...mapGetters({
      stateMapping: "pos/stockCounts/stateMapping",
      assignedStockProducts: "pos/assignedStockProducts/assignedStockProducts",
    }),

    isFormValid() {
      return true;
    },
    isEditable() {
      return this.stockCount && this.stockCount.status === "Draft";
    },
    isDeleteable() {
      return (
        this.stockCount &&
        (this.stockCount.status === "Draft" ||
          this.stockCount.status === "Active")
      );
    },
  },
  methods: {
    removeProduct(id) {
      this.stockCount.stockCountLines = this.stockCount.stockCountLines.filter(
        (x) => x.stockProduct.id !== id
      );
    },
    productSelected(line) {
      // Don't add if already exists
      if (
        this.stockCount.stockCountLines.find(
          (x) => x.stockProduct.id === line.stockProduct.id
        )
      ) {
        return;
      }

      this.stockCount.stockCountLines.push({ ...line, puomQuantity: 1 });
    },
    async show(stockCountId) {
      this.isActive = true;
      this.isLoading = true;

      var result = await this.$store.dispatch(
        "pos/stockCounts/getStockCount",
        stockCountId
      );

      this.stockCount = result;

      await this.loadExternalData();
      console.log("Got stock count", this.stockCount);
      this.isLoading = false;
    },
    async loadExternalData() {
      await this.$store.dispatch(
        "pos/assignedStockProducts/getAssignedStockProductsByVenue",
        this.stockCount.venueId
      );
      this.venue = await this.$store.dispatch(
        "pos/venues/getVenue",
        this.stockCount.venueId
      );
    },
    async save() {
      console.log("Saving DTO", this.stockCount);

      try {
        const result = await this.$store.dispatch(
          "pos/stockCounts/updateStockCount",
          {
            stockCountId: this.stockCount.id,
            stockCountDto: {
              stockCountLineProductIds: this.stockCount.stockCountLines.map(
                (e) => e.stockProductId
              ),
              deadlineDate: this.stockCount.deadlineDate,
              storageId: this.stockCount.storageId,
            },
          }
        );
        await this.$store.dispatch(
          "pos/stockCounts/getStockCountsByVenue",
          this.stockCount.venueId
        );

        // await this.showOrder(this.stockCount.id); // Refresh order line totals for this view

        this.$vs.notify({
          title: "Stock count saved",
          text: "Your stock count has been saved.",
        });
      } catch (err) {
        this.$vs.notify({
          title: "Error",
          text: `Failed to save stock count: ${err}`,
          color: "danger",
        });
      }
    },

    async activateStockCount() {
      try {
        await this.save();
        await this.$store.dispatch(
          "pos/stockCounts/activateStockCount",
          this.stockCount.id
        );
        await this.$store.dispatch(
          "pos/stockCounts/getStockCountsByVenue",
          this.stockCount.venueId
        );

        this.$vs.notify({
          title: "Stock count activated",
          text: "Your stock count has been activated.",
        });

        this.isActive = false;
        this.isLoading = true;
        this.stockCount = null;
      } catch (err) {
        this.$vs.notify({
          title: "Error",
          text: `Failed to activate stock count: ${err}`,
          color: "danger",
        });
      }
    },
  },
};
</script>
